import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip, TextField, MenuItem, IconButton } from "@mui/material";
import Swal from "sweetalert2";
import moment from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { currentTenant, userid, dataaccess, logo, userwhid, clientname } from '../../Common/Credentials.js';
import { spin } from '../../Common/NewLoader.jsx';
import { inventoryid } from '../../Common/Permissions.js';
import Header from '../../Component/Header.js';
import Menu from '../../Component/Menu.js';
import Footer from '../../Component/Footer.js';
import InventoryService from "../InventoryService.js";
import DeleteIcon from "../../images/Icons/delete.png";
import ViewIcon from "../../images/Icons/preview.png";

const StockIssueTable = () => {
  const [stockissue, setStockissue] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const navigate = useNavigate();

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshStocks = useCallback(() => {
    setLoading(true);
    let data = {
      tenantid: currentTenant,
      startdate: "",
      enddate: "",
      userid: userid,
      dataaccess: dataaccess,
      whid: userwhid,
      id: "",
      itemid: null,
      poupload: false
    };

    InventoryService.gettransfredstock(data)
      .then((response) => {
        const sortedData = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setStockissue(sortedData);
        setFilteredData(sortedData);
        setLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          Swal.fire({
            title: "Your Session is Lost: Please login Back to resume",
            text: ".",
            icon: "error",
            confirmButtonText: "Ok",
            confirmButtonColor: "#152f5f",
          }).then(() => navigate("/"));
        }
        setLoading(false);
      });
  }, [currentTenant, userid, dataaccess, userwhid, navigate]);

  const deleteStockIssue = (id, transferType) => {
    Swal.fire({
      title: `Delete ${transferType} Record?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        // Call delete API here
        Swal.fire({
          title: "Deleted!",
          text: "Stock issue record has been deleted.",
          icon: "success",
          confirmButtonText: "Ok",
          confirmButtonColor: "#152f5f",
        }).then(() => {
          setLoading(true);
          refreshStocks();
          setMessage("Stock Issue Record Deleted Successfully");
        });
      }
    });
  };

  const generatePDF = () => {
    const curdate = moment().format('DD-MM-YYYY HH:mm:ss');
    const columns = [
      { header: "Transfer Type", dataKey: 'stocktransfertype' },
      { header: "Issued By", dataKey: 'createdbyname' },
      { header: "Issued To", dataKey: 'receivedbyname' },
      { header: "From Org Unit", dataKey: 'fromwhname' },
      { header: "To Org Unit", dataKey: 'towhname' },
      { header: "Department", dataKey: 'deptname' },
      { header: "Date", dataKey: "createdAt" }
    ];

    const formattedData = stockissue.map(item => ({
      ...item,
      createdAt: moment(item.createdAt).format('DD-MM-YYYY hh:mm')
    }));

    var doc = new jsPDF("p", "pt", [842, 595]);
    autoTable(doc, {
      theme: 'grid',
      startY: 120,
      didDrawPage: (HookData) => {
        HookData.settings.margin.top = 120;
      },
      body: formattedData,
      columns: columns,
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageSize = doc.internal.pageSize;
      const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
      const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
      const footer = `Page ${i} of ${pageCount}`;

      doc.addImage(logo, "GIF", 40, 20, 60, 60, { baseline: "top" });
      doc.setFontSize(8);
      doc.text(`${curdate}`, 560, 20, 'right');
      doc.setFontSize(16);
      doc.text(`${clientname}`, 180, 40, { baseline: "top" });
      doc.setFontSize(14);
      doc.text(`Stock Issues`, 210, 70, { baseline: "top" });
      doc.line(0, 90, 600, 90);
      doc.setFontSize(10);

      doc.text(
        footer,
        pageWidth / 2 - doc.getTextWidth(footer) / 2,
        pageHeight - 15,
        { baseline: "bottom" }
      );
    }
    doc.save("StockIssues.pdf");
  };

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    refreshStocks();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshStocks]);

  useEffect(() => {
    updateFilteredData();
  }, [stockissue, searchTerm]);

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      if (sortConfig.key === 'createdAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = stockissue;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = stockissue.filter(item =>
        item.stocktransfertype?.toLowerCase().includes(lowerSearchTerm) ||
        item.createdbyname?.toLowerCase().includes(lowerSearchTerm) ||
        item.receivedbyname?.toLowerCase().includes(lowerSearchTerm) ||
        item.deptname?.toLowerCase().includes(lowerSearchTerm)
      );
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredData);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredData.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} records
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <TextField
            select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            variant="outlined"
            size="small"
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <MenuItem value={5}>5 per page</MenuItem>
            <MenuItem value={10}>10 per page</MenuItem>
            <MenuItem value={20}>20 per page</MenuItem>
            <MenuItem value={50}>50 per page</MenuItem>
          </TextField>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredData.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No stock issue records found matching your search' : 'No stock issue records available'}
        </div>
      );
    }

    const columnWidths = isMobile
      ? ['25%', '20%', '20%', '15%', '20%']
      : ['15%', '12%', '12%', '12%', '12%', '12%', '10%', '15%'];

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`} style={{ tableLayout: 'fixed' }}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'stocktransfertype' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('stocktransfertype')}
                  style={{ cursor: 'pointer', width: columnWidths[0] }}
                >
                  <div className="UI-th-content">
                    Transfer Type
                    <SortIndicator columnKey="stocktransfertype" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'createdbyname' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('createdbyname')}
                  style={{ cursor: 'pointer', width: columnWidths[1] }}
                >
                  <div className="UI-th-content">
                    Issued By
                    <SortIndicator columnKey="createdbyname" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'receivedbyname' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('receivedbyname')}
                  style={{ cursor: 'pointer', width: columnWidths[2] }}
                >
                  <div className="UI-th-content">
                    Issued To
                    <SortIndicator columnKey="receivedbyname" />
                  </div>
                </th>
                {!isMobile && (
                  <>
                    <th
                      className={`UI-th ${sortConfig.key === 'fromwhname' ? 'UI-th-sorted' : ''}`}
                      onClick={() => sortData('fromwhname')}
                      style={{ cursor: 'pointer', width: columnWidths[3] }}
                    >
                      <div className="UI-th-content">
                        From Org Unit
                        <SortIndicator columnKey="fromwhname" />
                      </div>
                    </th>
                    <th
                      className={`UI-th ${sortConfig.key === 'towhname' ? 'UI-th-sorted' : ''}`}
                      onClick={() => sortData('towhname')}
                      style={{ cursor: 'pointer', width: columnWidths[4] }}
                    >
                      <div className="UI-th-content">
                        To Org Unit
                        <SortIndicator columnKey="towhname" />
                      </div>
                    </th>
                    <th
                      className={`UI-th ${sortConfig.key === 'deptname' ? 'UI-th-sorted' : ''}`}
                      onClick={() => sortData('deptname')}
                      style={{ cursor: 'pointer', width: columnWidths[5] }}
                    >
                      <div className="UI-th-content">
                        Department
                        <SortIndicator columnKey="deptname" />
                      </div>
                    </th>
                  </>
                )}
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'status' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('status')}
                  style={{ cursor: 'pointer', width: isMobile ? columnWidths[3] : columnWidths[6] }}
                >
                  <div className="UI-th-content">
                    Status
                    <SortIndicator columnKey="status" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'createdAt' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('createdAt')}
                  style={{ cursor: 'pointer', width: isMobile ? columnWidths[4] : columnWidths[7] }}
                >
                  <div className="UI-th-content">
                    Date
                    <SortIndicator columnKey="createdAt" />
                  </div>
                </th>
                {(inventoryid === "2" || inventoryid === "3") && (
                  <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`} style={{ width: '10%' }}>
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((item) => (
                <tr
                  key={item.id}
                  className={`UI-table-row ${hoveredRow === item.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''}`}
                  onMouseEnter={() => setHoveredRow(item.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  <td
                    className={`UI-td UI-service-name ${hoveredRow === item.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                    style={{ width: columnWidths[0] }}
                  >
                    {highlightSearchText(item.stocktransfertype, searchTerm)}
                  </td>
                  <td
                    className={`UI-td ${hoveredRow === item.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                    style={{ width: columnWidths[1] }}
                  >
                    {highlightSearchText(item.createdbyname, searchTerm)}
                  </td>
                  <td
                    className={`UI-td ${hoveredRow === item.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                    style={{ width: columnWidths[2] }}
                  >
                    {highlightSearchText(item.receivedbyname, searchTerm)}
                  </td>
                  {!isMobile && (
                    <>
                      <td
                        className={`UI-td ${hoveredRow === item.id ? 'UI-service-name-hover' : ''}`}
                        style={{ width: columnWidths[3] }}
                      >
                        {item.fromwhname}
                      </td>
                      <td
                        className={`UI-td ${hoveredRow === item.id ? 'UI-service-name-hover' : ''}`}
                        style={{ width: columnWidths[4] }}
                      >
                        {item.towhname}
                      </td>
                      <td
                        className={`UI-td ${hoveredRow === item.id ? 'UI-service-name-hover' : ''}`}
                        style={{ width: columnWidths[5] }}
                      >
                        {item.deptname}
                      </td>
                    </>
                  )}
                  <td
                    className={`UI-td ${hoveredRow === item.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                    style={{ width: isMobile ? columnWidths[3] : columnWidths[6] }}
                  >
                    <span className={`UI-status ${item.status?.toLowerCase()}`}>
                      {item.status}
                    </span>
                  </td>
                  <td
                    className={`UI-td ${hoveredRow === item.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                    style={{ width: isMobile ? columnWidths[4] : columnWidths[7] }}
                  >
                    {moment(item.createdAt).format('DD-MM-YYYY HH:mm')}
                  </td>
                  {(inventoryid === "2" || inventoryid === "3") && (
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`} style={{ width: '10%' }}>
                      <div className="UI-action-buttons" style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                        <Tooltip
                          title="Delete Record"
                          arrow
                          slotProps={{
                            popper: {
                              sx: {
                                pointerEvents: 'none',
                              },
                            },
                          }}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteStockIssue(item.id, item.stocktransfertype);
                            }}
                            className={`UI-action-button ${hoveredAction === `delete-${item.id}` ? 'UI-delete-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                            onMouseEnter={() => setHoveredAction(`delete-${item.id}`)}
                            onMouseLeave={() => setHoveredAction(null)}
                            style={{
                              minWidth: '28px',
                              minHeight: '28px',
                            }}
                          >
                            <img
                              src={DeleteIcon}
                              alt="Delete"
                              className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `delete-${item.id}` ? 'UI-action-icon-hover' : ''}`}
                              style={{ width: "20px", height: "20px" }}
                            />
                          </button>
                        </Tooltip>
                        <Tooltip
                          title="View Details"
                          arrow
                          slotProps={{
                            popper: {
                              sx: {
                                pointerEvents: 'none',
                              },
                            },
                          }}
                        >
                          <Link
                            to="/stockissueitemsview"
                            state={{
                              data: item
                            }}
                            className={`UI-action-button ${hoveredAction === `view-${item.id}` ? 'UI-view-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                            onMouseEnter={() => setHoveredAction(`view-${item.id}`)}
                            onMouseLeave={() => setHoveredAction(null)}
                            style={{
                              minWidth: '28px',
                              minHeight: '28px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <img
                              src={ViewIcon}
                              alt="View"
                              className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `view-${item.id}` ? 'UI-action-icon-hover' : ''}`}
                              style={{ width: "20px", height: "20px" }}
                            />
                          </Link>
                        </Tooltip>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
          <div className="UI-card">
            {/* Header */}
            <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
              <nav className={`UI-breadcrumb ${isMobile ? 'UI-breadcrumb-mobile' : ''}`}>
                <Link
                  to="/inventory"
                  className={`UI-breadcrumb-item ${hovered === 'home' ? 'UI-breadcrumb-item-hover' : ''}`}
                  onMouseEnter={() => setHovered('home')}
                  onMouseLeave={() => setHovered(null)}
                >
                  Home
                </Link>
                <span>/</span>
                <span className="UI-breadcrumb-item UI-breadcrumb-active">
                  Stock Issue/Transfer
                </span>
              </nav>

              <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
                <div>
                  <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                    Stock Issue/Transfer
                  </h1>
                  <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                    Manage stock issue and transfer records
                  </p>
                </div>

                <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
                  {/* Success Message */}
                  {message && (
                    <div className="text-center text-success text-uppercase mb-2">
                      {message}
                    </div>
                  )}

                  {/* Mobile Search */}
                  {isMobile && (
                    <input
                      type="text"
                      placeholder="Search stock issues..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="UI-mobile-search-input"
                    />
                  )}

                  <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                    {/* Desktop Search Toggle */}
                    {!isMobile && (
                      <div
                        className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                        onClick={() => {
                          if (!showSearch) setShowSearch(true);
                        }}
                        onMouseEnter={() => setHovered("search")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i
                          className="fa fa-search UI-search-icon"
                        />
                        {!showSearch && "Search"}
                        {showSearch && (
                          <input
                            type="text"
                            placeholder="Search stock issues..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="UI-search-input"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => {
                              if (searchTerm.trim() === "") {
                                setShowSearch(false);
                              }
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* PDF Export button */}

                    <button
                      onClick={generatePDF}
                      className={`UI-btn ${isMobile ? 'UI-btn-mobile' : ''} UI-btn-outline ${hovered === "pdf" ? 'UI-btn-outline-hover' : ''
                        }`}
                      onMouseEnter={() => setHovered("pdf")}
                      onMouseLeave={() => setHovered(null)}
                    >
                      <i className="fa fa-file-pdf" /> {isMobile ? "PDF" : "Export"}
                    </button>


                    {/* New Stock Issue button */}
                    {(inventoryid === "2" || inventoryid === "3") && (
                      <Link
                        to="/addstockissue"
                        state={{ id: -1, pagename: 'New Stock Issue' }}
                        className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                        onMouseEnter={() => setHovered("new")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i className="fa fa-plus" /> {isMobile ? "New" : "New Issue"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="UI-loading-container">
                {spin}
              </div>
            ) : (
              renderTable()
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default StockIssueTable;